import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Send, Paperclip, Smile, Image, MoreVertical, Phone, Video, User ,userAvatar, User2Icon} from 'lucide-react';
import { authAPI } from '../../services/api';

import expert1 from '../../assets/team.png';

import './dashboardComponents/chat.css';

export default function ExpertChat() {
  const { expertId } = useParams();
  const location = useLocation();
  const isExpertDashboard = location.pathname.startsWith('/expert-dashboard');
  const backPath = isExpertDashboard ? '/expert-dashboard/messages' : '/farmer-dashboard/messages';

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [expert, setExpert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (isExpertDashboard) {
          // Expert side: other user is a farmer — get their name from conversations list
          const conversationsResult = await authAPI.getConversations();
          if (conversationsResult.success) {
            const conversation = conversationsResult.data.find(conv => conv.user.id === parseInt(expertId));
            if (conversation) {
              setExpert({
                id: conversation.user.id,
                name: conversation.user.name,
                image: conversation.user.avatar || expert1,
                specialty: 'Farmer',
                status: 'online'
              });
              setConversationId(conversation.conversation_id);

              const messagesResult = await authAPI.getConversationMessages(conversation.conversation_id);
              if (messagesResult.success) {
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const currentUserId = parseInt(currentUser.id);
                setMessages(messagesResult.data.map(msg => ({
                  id: msg.id,
                  sender: parseInt(msg.sender_id) === currentUserId ? 'user' : 'expert',
                  content: msg.content,
                  timestamp: new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                  isRead: msg.is_read,
                })));
              }
            } else {
              setConversationId(null);
              setMessages([]);
            }
          }
        } else {
          // Farmer side: other user is an expert — fetch expert details
          const expertResult = await authAPI.getExpertDetails(expertId);
          if (expertResult.success) {
            const expertData = expertResult.data;
            setExpert({
              id: expertData.expert_id,
              name: expertData.receiver_name,
              image: expertData.receiver_image || expert1,
              specialty: expertData.specialization || 'Expert',
              status: 'online'
            });
          }

          const conversationsResult = await authAPI.getConversations();
          if (conversationsResult.success) {
            const conversation = conversationsResult.data.find(conv => conv.user.id === parseInt(expertId));
            if (conversation) {
              setConversationId(conversation.conversation_id);

              const messagesResult = await authAPI.getConversationMessages(conversation.conversation_id);
              if (messagesResult.success) {
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const currentUserId = parseInt(currentUser.id);
                setMessages(messagesResult.data.map(msg => ({
                  id: msg.id,
                  expertId: parseInt(expertId),
                  sender: parseInt(msg.sender_id) === currentUserId ? 'user' : 'expert',
                  content: msg.content,
                  timestamp: new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                  date: new Date(msg.created_at).toLocaleDateString() === new Date().toLocaleDateString() ? 'Today' : new Date(msg.created_at).toLocaleDateString(),
                  isRead: msg.is_read,
                  senderName: msg.sender ? `${msg.sender.first_name} ${msg.sender.last_name}` : 'Unknown'
                })));
              }
            } else {
              setConversationId(null);
              setMessages([]);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (expertId) {
      fetchData();
    }
  }, [expertId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === '' || !expertId) return;

    try {
      setSending(true);
      
      const messageData = {
        receiver_id: parseInt(expertId),
        content: newMessage.trim()
      };

      const result = await authAPI.sendMessage(messageData);
      
      if (result.success) {
        // If this is a new conversation, update the conversationId
        if (!conversationId && result.data && result.data.conversation_id) {
          setConversationId(result.data.conversation_id);
        }
        
        // Add message to local state
        const newMsg = {
          id: Date.now(), // Temporary ID
          expertId: parseInt(expertId),
          sender: 'user',
          content: newMessage.trim(),
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          date: 'Today',
          isRead: false
        };

        setMessages(prev => [...prev, newMsg]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="loading-container_chat">Loading conversation...</div>;
  }

  if (!expert) {
    return <div className="loading-container_chat">Expert not found...</div>;
  }

  return (
    <div className="chat-page-container_chat">
      <div className="chat-header_chat">
        <div className="header-left_chat">
          <Link to={backPath} className="back-button_chat">
            <ArrowLeft />
          </Link>
          <div className="expert-info_chat">
            <img src={expert.image} alt={expert.name} className="expert-avatar_chat" />
            <div className="expert-details_chat">
              <div className="expert-name_chat">{expert.name}</div>
              <div className="expert-specialty_chat">{expert.specialty}</div>
            </div>
          </div>
        </div>
      {/*   <div className="header-actions_chat">
          <button className="action-button_chat phone-action_chat">
            <Phone size={20} />
          </button>
          <button className="action-button_chat video-action_chat">
            <Video size={20} />
          </button>
          <button className="action-button_chat more-action_chat">
            <MoreVertical size={20} />
          </button>
        </div> */}
      </div>

      <div className="chat-messages-container_chat">
        {messages.length > 0 ? (
          <>
            <div className="date-separator_chat">
              <span>Today</span>
            </div>

            {messages.map(message => (
              <div 
                key={message.id} 
                className={`message-bubble_chat ${message.sender === 'user' ? 'user-message_chat' : 'expert-message_chat'}`}
              >
                {message.sender === 'expert' && (
                  <div className="message-avatar_chat">
                    <img 
                      src={expert?.image || expert1} 
                      alt={expert?.name || 'Expert'} 
                    />
                  </div>
                )}
                <div className="message-content_chat">
                  <div className="message-text_chat">{message.content}</div>
                  <div className="message-time_chat">{message.timestamp}</div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="no-messages_chat">
            <p>No messages yet. Start the conversation!</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-container_chat" onSubmit={handleSendMessage}>
        <div className="attachment-actions_chat">
      {/*     <button type="button" className="attachment-button_chat">
            <Paperclip size={20} />
          </button>
          <button type="button" className="attachment-button_chat">
            <Image size={20} />
          </button>
          <button type="button" className="attachment-button_chat">
            <Smile size={20} />
          </button> */}
        </div>
        <input
          type="text"
          className="message-input_chat"
          placeholder="Type your message here..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          disabled={sending}
        />
        <button type="submit" className="send-button_chat" disabled={sending}>
          {sending ? (
            <div className="loading-spinner_chat"></div>
          ) : (
            <Send size={20} />
          )}
        </button>
      </form>

      <style jsx>{`
        .no-messages_chat {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
          color: #6b7280;
          font-size: 16px;
        }

        .loading-spinner_chat {
          width: 16px;
          height: 16px;
          border: 2px solid #ffffff;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .send-button_chat:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}